/**
 * Real-time Validation Service
 *
 * Provides utilities for validating CSV data in real-time before submission.
 * Enhanced with detailed error information, suggestions, and value type categorization.
 */
import { ValidationResult } from '../components/upload/ValidationFeedback';

interface ColumnMappingConfig {
  field: string;
  possibleNames: string[];
  required: boolean;
  defaultValue?: any;
  type: 'string' | 'number' | 'percentage' | 'currency' | 'date';
  description?: string;
  format?: string;
  validValues?: string[];
  minValue?: number;
  maxValue?: number;
}

export interface ValidationIssue {
  field: string;
  message: string;
  severity: 'error' | 'warning' | 'info';
  details?: string;
  suggestion?: string;
  valueType?: string;
}

// Error category constants for better identification of issue types
const ERROR_TYPES = {
  REQUIRED_FIELD_MISSING: 'required_field_missing',
  EMPTY_REQUIRED_VALUE: 'empty_required_value',
  INVALID_FORMAT: 'invalid_format',
  TYPE_MISMATCH: 'type_mismatch',
  VALUE_OUT_OF_RANGE: 'value_out_of_range',
  VALUE_NOT_IN_LIST: 'value_not_in_list',
  POTENTIAL_DUPLICATE: 'potential_duplicate',
  SUSPICIOUS_VALUE: 'suspicious_value',
  DATA_QUALITY: 'data_quality'
};

// Validation utility to check if a value appears to be a number/currency/percentage
function isNumericValue(value: any): boolean {
  if (value === null || value === undefined || value === '') {
    return false;
  }

  if (typeof value === 'number') {
    return true;
  }

  if (typeof value === 'string') {
    const cleanValue = value.trim();
    if (
      cleanValue.includes('$') ||
      cleanValue.includes('€') ||
      cleanValue.includes('£') ||
      cleanValue.endsWith('%') ||
      /^-?\d+(,\d+)*(\.\d+)?$/.test(cleanValue) ||
      /^-?\d+(\.\d+)*(,\d+)?$/.test(cleanValue)
    ) {
      return true;
    }
  }

  return false;
}

// Enhanced currency parsing with formatting suggestion
function parseCurrency(value: string): { isValid: boolean; parsedValue?: number; suggestion?: string } {
  if (!value || typeof value !== 'string') {
    return { isValid: false };
  }

  // Clean the string of currency symbols and commas
  const cleanValue = value.trim().replace(/[$€£¥₹]/g, '').replace(/,/g, '');

  // Try to parse the cleaned number
  const parsed = parseFloat(cleanValue);
  if (isNaN(parsed)) {
    return { isValid: false };
  }

  // Provide a correctly formatted suggestion
  const suggestion = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(parsed);

  return {
    isValid: true,
    parsedValue: parsed,
    suggestion
  };
}

// Enhanced percentage parsing with formatting suggestion
function parsePercentage(value: string): { isValid: boolean; parsedValue?: number; suggestion?: string } {
  if (!value || typeof value !== 'string') {
    return { isValid: false };
  }

  // Clean the string of percentage symbols and commas
  let cleanValue = value.trim();
  if (cleanValue.endsWith('%')) {
    cleanValue = cleanValue.slice(0, -1);
  }
  cleanValue = cleanValue.replace(/,/g, '');

  // Try to parse the cleaned number
  const parsed = parseFloat(cleanValue);
  if (isNaN(parsed)) {
    return { isValid: false };
  }

  // Provide correctly formatted percentage suggestion
  const suggestion = `${parsed.toFixed(2)}%`;

  return {
    isValid: true,
    parsedValue: parsed / 100, // Convert to decimal for internal use
    suggestion
  };
}

// Enhanced date validation with format detection and suggestion
function validateDate(value: any): { isValid: boolean; parsedDate?: Date; suggestion?: string; detectedFormat?: string } {
  if (value === null || value === undefined || value === '') {
    return { isValid: false };
  }

  if (value instanceof Date) {
    return {
      isValid: !isNaN(value.getTime()),
      parsedDate: value,
      suggestion: value.toISOString().split('T')[0] // YYYY-MM-DD format
    };
  }

  if (typeof value === 'string') {
    // Try parsing as ISO date first
    const isoDate = new Date(value);
    if (!isNaN(isoDate.getTime())) {
      return {
        isValid: true,
        parsedDate: isoDate,
        suggestion: isoDate.toISOString().split('T')[0],
        detectedFormat: 'ISO'
      };
    }

    // Try MM/DD/YYYY format
    const mmddyyyy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    if (mmddyyyy.test(value)) {
      const [_, month, day, year] = mmddyyyy.exec(value) || [];
      const date = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
      if (!isNaN(date.getTime())) {
        return {
          isValid: true,
          parsedDate: date,
          suggestion: date.toISOString().split('T')[0],
          detectedFormat: 'MM/DD/YYYY'
        };
      }
    }

    // Try DD/MM/YYYY format
    const ddmmyyyy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    if (ddmmyyyy.test(value)) {
      const [_, day, month, year] = ddmmyyyy.exec(value) || [];
      const date = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
      if (!isNaN(date.getTime())) {
        return {
          isValid: true,
          parsedDate: date,
          suggestion: date.toISOString().split('T')[0],
          detectedFormat: 'DD/MM/YYYY'
        };
      }
    }
  }

  return { isValid: false };
}

// Check for suspicious or problematic values
function detectSuspiciousValues(field: string, value: any): { isSuspicious: boolean; reason?: string; severity: 'warning' | 'error' } {
  if (typeof value !== 'string') {
    return { isSuspicious: false, severity: 'warning' };
  }

  const lowerValue = value.toLowerCase().trim();

  // Check for placeholder values
  if (['n/a', 'na', 'none', 'unknown', 'tbd', 'tba', 'test', 'todo', 'placeholder'].includes(lowerValue)) {
    return {
      isSuspicious: true,
      reason: `Value "${value}" appears to be a placeholder`,
      severity: 'warning'
    };
  }

  // Check for potentially sensitive information in non-sensitive fields
  const sensitivePattern = /password|secret|key|token|credit|ssn|social|security/i;
  if (sensitivePattern.test(field) || sensitivePattern.test(lowerValue)) {
    return {
      isSuspicious: true,
      reason: `Potential sensitive information detected`,
      severity: 'error'
    };
  }

  return { isSuspicious: false, severity: 'warning' };
}

class RealTimeValidationService {
  /**
   * Validate a mapped CSV row against field configurations with enhanced error reporting
   */
  validateRow(
    row: Record<string, any>,
    columnMapping: Record<string, string>,
    fieldConfigs: Record<string, ColumnMappingConfig>,
    rowIndex: number
  ): ValidationResult {
    const issues: ValidationIssue[] = [];

    // Check each field mapping
    Object.entries(fieldConfigs).forEach(([field, config]) => {
      // Get the corresponding CSV header
      const headerName = columnMapping[field];

      // If required field isn't mapped, that's an error
      if (!headerName && config.required) {
        issues.push({
          field,
          message: `Required field "${field}" is not mapped to any CSV column`,
          severity: 'error',
          details: `This field is required for data processing. Please select a column from your CSV that contains ${field.replace(/_/g, ' ')} data.`,
          valueType: ERROR_TYPES.REQUIRED_FIELD_MISSING
        });
        return;
      }

      // Skip validation for unmapped fields
      if (!headerName) return;

      // Get the value from the row
      const value = row[headerName];

      // Check for required fields that are empty
      if (config.required && (value === null || value === undefined || value === '')) {
        issues.push({
          field,
          message: `Required field "${field}" is empty in this row`,
          severity: 'error',
          details: `Row ${rowIndex} has no value for "${field}". Either provide a value or use a default value if appropriate.`,
          valueType: ERROR_TYPES.EMPTY_REQUIRED_VALUE,
          suggestion: config.defaultValue !== undefined ? `${config.defaultValue}` : undefined
        });
        return;
      }

      // Skip type validation for empty values in non-required fields
      if (value === null || value === undefined || value === '') return;

      // Check for suspicious values
      const suspiciousCheck = detectSuspiciousValues(field, value);
      if (suspiciousCheck.isSuspicious) {
        issues.push({
          field,
          message: suspiciousCheck.reason || `Suspicious value detected: "${value}"`,
          severity: suspiciousCheck.severity,
          details: `This value may cause issues in processing or reporting.`,
          valueType: ERROR_TYPES.SUSPICIOUS_VALUE
        });
      }

      // Type-specific validation with enhanced errors and suggestions
      switch (config.type) {
        case 'number':
          if (!isNumericValue(value)) {
            issues.push({
              field,
              message: `Value "${value}" should be a number`,
              severity: 'error',
              details: `Expected a numeric value but found "${value}". Please provide a valid number.`,
              valueType: ERROR_TYPES.TYPE_MISMATCH,
              suggestion: typeof value === 'string' ? value.replace(/[^\d.-]/g, '') : undefined
            });
          } else if (typeof value === 'string') {
            // Check if within range if specified
            const numValue = parseFloat(value.replace(/[^\d.-]/g, ''));
            if (!isNaN(numValue)) {
              if (config.minValue !== undefined && numValue < config.minValue) {
                issues.push({
                  field,
                  message: `Value ${numValue} is below the minimum of ${config.minValue}`,
                  severity: 'warning',
                  details: `The value is outside the expected range. This may indicate a data entry error.`,
                  valueType: ERROR_TYPES.VALUE_OUT_OF_RANGE,
                  suggestion: `${config.minValue}`
                });
              }

              if (config.maxValue !== undefined && numValue > config.maxValue) {
                issues.push({
                  field,
                  message: `Value ${numValue} exceeds the maximum of ${config.maxValue}`,
                  severity: 'warning',
                  details: `The value is outside the expected range. This may indicate a data entry error.`,
                  valueType: ERROR_TYPES.VALUE_OUT_OF_RANGE,
                  suggestion: `${config.maxValue}`
                });
              }
            }
          }
          break;

        case 'currency':
          if (typeof value === 'string') {
            const currencyResult = parseCurrency(value);
            if (!currencyResult.isValid) {
              issues.push({
                field,
                message: `Value "${value}" should be a currency amount`,
                severity: 'error',
                details: `Expected a currency value like "$1,234.56". Please format the amount correctly.`,
                valueType: ERROR_TYPES.TYPE_MISMATCH,
                suggestion: currencyResult.suggestion || '$0.00'
              });
            } else if (currencyResult.parsedValue !== undefined) {
              // Range check for currency values
              if (config.minValue !== undefined && currencyResult.parsedValue < config.minValue) {
                issues.push({
                  field,
                  message: `Amount ${currencyResult.suggestion} is below the minimum of $${config.minValue.toFixed(2)}`,
                  severity: 'warning',
                  details: `The value is unusually low. Please verify this is correct.`,
                  valueType: ERROR_TYPES.VALUE_OUT_OF_RANGE,
                  suggestion: new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  }).format(config.minValue)
                });
              }

              if (config.maxValue !== undefined && currencyResult.parsedValue > config.maxValue) {
                issues.push({
                  field,
                  message: `Amount ${currencyResult.suggestion} exceeds the maximum of $${config.maxValue.toFixed(2)}`,
                  severity: 'warning',
                  details: `The value is unusually high. Please verify this is correct.`,
                  valueType: ERROR_TYPES.VALUE_OUT_OF_RANGE,
                  suggestion: new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  }).format(config.maxValue)
                });
              }
            }
          } else if (!isNumericValue(value)) {
            issues.push({
              field,
              message: `Value "${value}" should be a currency amount`,
              severity: 'error',
              details: `Expected a currency value. Please provide a valid amount.`,
              valueType: ERROR_TYPES.TYPE_MISMATCH,
              suggestion: '$0.00'
            });
          }
          break;

        case 'percentage':
          if (typeof value === 'string') {
            const percentageResult = parsePercentage(value);
            if (!percentageResult.isValid) {
              issues.push({
                field,
                message: `Value "${value}" should be a percentage`,
                severity: 'error',
                details: `Expected a percentage value like "42.5%" or "42.5". Please format correctly.`,
                valueType: ERROR_TYPES.TYPE_MISMATCH,
                suggestion: percentageResult.suggestion || '0.00%'
              });
            } else if (percentageResult.parsedValue !== undefined) {
              // Range check for percentage values (in decimal form)
              if (config.minValue !== undefined && percentageResult.parsedValue < config.minValue) {
                issues.push({
                  field,
                  message: `Percentage ${percentageResult.suggestion} is below the minimum of ${(config.minValue * 100).toFixed(2)}%`,
                  severity: 'warning',
                  details: `The percentage is unusually low. Please verify this is correct.`,
                  valueType: ERROR_TYPES.VALUE_OUT_OF_RANGE,
                  suggestion: `${(config.minValue * 100).toFixed(2)}%`
                });
              }

              if (config.maxValue !== undefined && percentageResult.parsedValue > config.maxValue) {
                issues.push({
                  field,
                  message: `Percentage ${percentageResult.suggestion} exceeds the maximum of ${(config.maxValue * 100).toFixed(2)}%`,
                  severity: 'warning',
                  details: `The percentage is unusually high. Please verify this is correct.`,
                  valueType: ERROR_TYPES.VALUE_OUT_OF_RANGE,
                  suggestion: `${(config.maxValue * 100).toFixed(2)}%`
                });
              }
            }
          } else if (!isNumericValue(value)) {
            issues.push({
              field,
              message: `Value "${value}" should be a percentage`,
              severity: 'error',
              details: `Expected a percentage value. Please provide a valid percentage.`,
              valueType: ERROR_TYPES.TYPE_MISMATCH,
              suggestion: '0.00%'
            });
          }
          break;

        case 'date': {
          const dateResult = validateDate(value);
          if (!dateResult.isValid) {
            issues.push({
              field,
              message: `Value "${value}" is not a valid date`,
              severity: 'error',
              details: `Expected a date in YYYY-MM-DD, MM/DD/YYYY, or DD/MM/YYYY format.`,
              valueType: ERROR_TYPES.TYPE_MISMATCH,
              suggestion: new Date().toISOString().split('T')[0] // Current date as suggestion
            });
          } else if (dateResult.parsedDate && config.format && dateResult.detectedFormat !== config.format) {
            issues.push({
              field,
              message: `Date "${value}" should use ${config.format} format`,
              severity: 'warning',
              details: `The date was parsed correctly but uses ${dateResult.detectedFormat} format instead of the preferred ${config.format} format.`,
              valueType: ERROR_TYPES.INVALID_FORMAT,
              suggestion: dateResult.suggestion
            });
          }
          break;
        }

        // String validation including enum validation
        case 'string':
          if (typeof value !== 'string') {
            issues.push({
              field,
              message: `Value should be text, found ${typeof value}`,
              severity: 'warning',
              details: `Expected a text value but found a different type.`,
              valueType: ERROR_TYPES.TYPE_MISMATCH,
              suggestion: String(value)
            });
          } else if (config.validValues && !config.validValues.includes(value)) {
            // Find closest match for suggestion
            let closestMatch = '';
            let minDistance = Infinity;

            // Simple edit distance for finding closest valid value
            for (const validValue of config.validValues) {
              // Calculate string similarity (very basic approach)
              const distance = this.getEditDistance(value.toLowerCase(), validValue.toLowerCase());
              if (distance < minDistance) {
                minDistance = distance;
                closestMatch = validValue;
              }
            }

            issues.push({
              field,
              message: `"${value}" is not in the list of valid values for ${field}`,
              severity: 'warning',
              details: `Expected one of: ${config.validValues.join(', ')}`,
              valueType: ERROR_TYPES.VALUE_NOT_IN_LIST,
              suggestion: minDistance < 5 ? closestMatch : undefined
            });
          }
          break;
      }
    });

    return {
      rowIndex,
      issues
    };
  }

  /**
   * Calculate edit distance between two strings (Levenshtein distance)
   * Used for finding closest matches when suggesting corrections
   */
  private getEditDistance(a: string, b: string): number {
    if (a.length === 0) return b.length;
    if (b.length === 0) return a.length;

    const matrix = [];

    // Initialize matrix
    for (let i = 0; i <= b.length; i++) {
      matrix[i] = [i];
    }

    for (let j = 0; j <= a.length; j++) {
      matrix[0][j] = j;
    }

    // Fill in the matrix
    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        if (b.charAt(i - 1) === a.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1, // substitution
            matrix[i][j - 1] + 1,     // insertion
            matrix[i - 1][j] + 1      // deletion
          );
        }
      }
    }

    return matrix[b.length][a.length];
  }

  /**
   * Check for potential data quality issues like duplicates
   */
  checkDataQuality(
    data: Record<string, any>[],
    columnMapping: Record<string, string>,
    fieldConfigs: Record<string, ColumnMappingConfig>
  ): ValidationResult[] {
    const results: ValidationResult[] = [];

    // Check for potential duplicates by identifying rows with identical key fields
    const keyFields = Object.entries(fieldConfigs)
      .filter(([_, config]) => config.required)
      .map(([field]) => field);

    if (keyFields.length === 0 || data.length < 2) {
      return results; // Not enough data or key fields to detect duplicates
    }

    // Build a map of key values to row indices
    const keyValueMap: Record<string, number[]> = {};

    data.forEach((row, rowIndex) => {
      // Build a composite key from mapped values
      const keyValues = keyFields
        .map(field => {
          const headerName = columnMapping[field];
          return headerName ? String(row[headerName] || '') : '';
        })
        .filter(value => value !== '');

      // Skip if we couldn't extract any key values
      if (keyValues.length === 0) return;

      const compositeKey = keyValues.join('|');

      if (!keyValueMap[compositeKey]) {
        keyValueMap[compositeKey] = [];
      }

      keyValueMap[compositeKey].push(rowIndex);
    });

    // Find duplicate rows
    Object.entries(keyValueMap)
      .filter(([_, rowIndices]) => rowIndices.length > 1)
      .forEach(([compositeKey, rowIndices]) => {
        // Create an issue for each duplicate (except the first one)
        rowIndices.slice(1).forEach(rowIndex => {
          const issues: ValidationIssue[] = [{
            field: 'multiple',
            message: `Potential duplicate data detected`,
            severity: 'warning',
            details: `This row appears similar to row ${rowIndices[0] + 1}. Key fields have identical values.`,
            valueType: ERROR_TYPES.POTENTIAL_DUPLICATE
          }];

          results.push({
            rowIndex: rowIndex + 1, // Adjust from 0-based to 1-based for display
            issues
          });
        });
      });

    return results;
  }

  /**
   * Validate an entire CSV dataset with enhanced error reporting
   */
  validateCSVData(
    data: Record<string, any>[],
    columnMapping: Record<string, string>,
    fieldConfigs: Record<string, ColumnMappingConfig>,
    sampleSize = 0
  ): ValidationResult[] {
    const results: ValidationResult[] = [];

    // Determine how many rows to validate (all or sample)
    const rowsToValidate = sampleSize > 0 ? Math.min(data.length, sampleSize) : data.length;

    // Validate each row
    for (let i = 0; i < rowsToValidate; i++) {
      const result = this.validateRow(data[i], columnMapping, fieldConfigs, i + 1);

      // Only keep results with issues
      if (result.issues.length > 0) {
        results.push(result);
      }
    }

    // Add data quality checks if validating more than one row
    if (rowsToValidate > 1) {
      const qualityIssues = this.checkDataQuality(
        data.slice(0, rowsToValidate),
        columnMapping,
        fieldConfigs
      );

      results.push(...qualityIssues);
    }

    return results;
  }

  /**
   * Check if the mapping configuration is complete with enhanced error information
   */
  validateMappingCompleteness(
    columnMapping: Record<string, string>,
    fieldConfigs: Record<string, ColumnMappingConfig>
  ): ValidationResult | null {
    const requiredFields = Object.entries(fieldConfigs)
      .filter(([_, config]) => config.required)
      .map(([field, config]) => ({ field, config }));

    const missingRequiredFields = requiredFields.filter(({ field }) => !columnMapping[field]);

    if (missingRequiredFields.length === 0) {
      return null;
    }

    const issues = missingRequiredFields.map(({ field, config }) => ({
      field,
      message: `Required field "${field}" must be mapped to a CSV column`,
      severity: 'error' as const,
      details: `This is a required field for data processing. ${config.description || ''}`,
      valueType: ERROR_TYPES.REQUIRED_FIELD_MISSING,
      suggestion: config.possibleNames[0] || field.replace(/_/g, ' ')
    }));

    return {
      rowIndex: 0,
      issues
    };
  }

  /**
   * Generate summary statistics for validation results with enhanced metrics
   */
  summarizeValidationResults(results: ValidationResult[]) {
    const allIssues = results.flatMap(result => result.issues);

    const totalIssues = allIssues.length;
    const errorCount = allIssues.filter(i => i.severity === 'error').length;
    const warningCount = allIssues.filter(i => i.severity === 'warning').length;
    const infoCount = allIssues.filter(i => i.severity === 'info').length;

    // Count issues by type
    const issuesByType: Record<string, number> = {};
    allIssues.forEach(issue => {
      if (issue.valueType) {
        issuesByType[issue.valueType] = (issuesByType[issue.valueType] || 0) + 1;
      }
    });

    // Count affected rows
    const rowsWithErrors = new Set(
      results
        .filter(result => result.issues.some(issue => issue.severity === 'error'))
        .map(result => result.rowIndex)
    ).size;

    const rowsWithWarnings = new Set(
      results
        .filter(result =>
          result.issues.some(issue => issue.severity === 'warning') &&
          !result.issues.some(issue => issue.severity === 'error')
        )
        .map(result => result.rowIndex)
    ).size;

    return {
      totalRows: results.length,
      totalIssues,
      errorCount,
      warningCount,
      infoCount,
      rowsWithErrors,
      rowsWithWarnings,
      issuesByType,
      isValid: errorCount === 0,
      hasWarnings: warningCount > 0,
      percentErrorRows: results.length > 0 ? (rowsWithErrors / results.length) * 100 : 0
    };
  }

  /**
   * Suggest column mappings based on headers and field configurations
   */
  suggestColumnMappings(
    csvHeaders: string[],
    fieldConfigs: Record<string, ColumnMappingConfig>
  ): Record<string, string> {
    const suggestions: Record<string, string> = {};

    if (!csvHeaders.length || !Object.keys(fieldConfigs).length) {
      return suggestions;
    }

    // For each field, find the best matching header
    Object.entries(fieldConfigs).forEach(([field, config]) => {
      const possibleNames = config.possibleNames || [field.replace(/_/g, ' ')];

      // Try to find an exact match first (case-insensitive)
      const exactMatch = csvHeaders.find(header =>
        possibleNames.some(name => name.toLowerCase() === header.toLowerCase())
      );

      if (exactMatch) {
        suggestions[field] = exactMatch;
        return;
      }

      // Try to find a partial match
      const partialMatches = csvHeaders.filter(header =>
        possibleNames.some(name =>
          header.toLowerCase().includes(name.toLowerCase()) ||
          name.toLowerCase().includes(header.toLowerCase())
        )
      );

      if (partialMatches.length === 1) {
        suggestions[field] = partialMatches[0];
      } else if (partialMatches.length > 1) {
        // If multiple matches, pick the one with the closest length to the field name
        const closestMatch = partialMatches.sort((a, b) => {
          const aDiff = Math.abs(a.length - field.length);
          const bDiff = Math.abs(b.length - field.length);
          return aDiff - bDiff;
        })[0];

        suggestions[field] = closestMatch;
      }
      // No suggestion if no matches found
    });

    return suggestions;
  }
}

export default new RealTimeValidationService();